import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useEditPlanOnMapModalStore } from './Modals/EditPlanOnMap/EditPlanOnMapStore';
import { useDeletePlanOnMapModalStore } from './Modals/DeletPlanOnMap/DeletePlanOnMapStore';

import { Plan } from 'api/clients/types';
import { MLButton } from 'components/Button/Button';

interface PlanSelectorProps {
  slug: string;
  currentPlan: Plan;
  plans: Plan[];
  selectedPlanSlug: string;
  onPlanSelect: (planSlug: string) => void;
}

export const PlanSelector: React.FC<PlanSelectorProps> = ({
  slug,
  currentPlan,
  plans,
  selectedPlanSlug,
  onPlanSelect,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { onCompleteVulOpen } = useEditPlanOnMapModalStore();
  const { onDeleteVulOpen } = useDeletePlanOnMapModalStore();
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePlanClick = (planSlug: string) => {
    onPlanSelect(planSlug);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      title="Plans"
      style={{
        position: 'absolute',
        top: '10px',
        left: '55px',
        zIndex: 1000,
      }}
    >
      <button className="map-dropdown-toggle" onClick={() => setIsOpen(!isOpen)} style={{ position: 'relative' }}>
        {currentPlan ? currentPlan.title : t('PlanSelector.choosePlan')} ▾
      </button>

      {isOpen && (
        <div
          className="map-popup-content"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: '200px',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            marginTop: '4px',
          }}
        >
          {plans?.map(plan => (
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                borderBottom: '1px solid #eee',
              }}
              className={`d-flex align-items-center justify-content-center gap-2 plan-item ${plan.slug === selectedPlanSlug ? 'active' : ''}`}
              key={plan.slug}
              onClick={() => handlePlanClick(plan.slug)}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.color = '#000';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div>{plan.title}</div>
              <button
                type="button"
                className="btn btn-sm btn-ghost-primary btn-icon"
                onClick={e => {
                  e.stopPropagation();
                  onCompleteVulOpen(plan.slug);
                }}
                title={t('PlanSelector.editPlan')}
              >
                <i className="ri-pencil-fill icon icon__edit" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost-primary btn-icon"
                onClick={e => {
                  e.stopPropagation();
                  onDeleteVulOpen(plan.id, plan.slug);
                }}
                title={t('PlanSelector.deletePlan')}
              >
                <i className="ri-delete-bin-6-fill icon icon__delete" />
              </button>
            </div>
          ))}

          <div className="d-flex justify-content-center" style={{ padding: '8px 12px' }}>
            <MLButton
              color="purple"
              isGhost
              onClick={() => (window.location.href = `/r/object/create-plan/${slug}?no_plan_redirect=true`)}
              innerText={t('PlanSelector.addNewPlan')}
            />
          </div>
        </div>
      )}
    </div>
  );
};
